import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Avatar,
  Space,
  Typography,
  Tooltip,
  Skeleton,
} from "antd";
import {
  UpgradePlanCard,
  CopilotsUsageCard,
  CategoriesUsageCard,
  UsageOverTime,
} from "./components/index.jsx";
import AchievementsCard from "@components/Cards/gamification/AchievementsCard";
import ReferralCard from "@components/Cards/gamification/ReferralCard";
import DailyRewardsCard from "@components/Cards/gamification/DailyRewardsCard";
import { InfoNotification } from "@components";
import { useSelector } from "react-redux";
import {
  PlayCircleOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  GiftOutlined,
  ChromeOutlined,
  FacebookOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import slideAvatar1 from "@assets/images/memoji/user-avatar-1.png";
import slideAvatar2 from "@assets/images/memoji/user-avatar-2.png";
import slideAvatar3 from "@assets/images/memoji/user-avatar-3.png";
import Chrome from "@assets/images/pages/apps/chrome.png";
import Firefox from "@assets/images/pages/apps/firefox.png";
import Edge from "@assets/images/pages/apps/edge.png";
import Brave from "@assets/images/pages/apps/brave.png";
import Opera from "@assets/images/pages/apps/opera.png";
import Safari from "@assets/images/pages/apps/safari.png";
import { useTranslation } from "react-i18next";
import "@assets/css/admin.css";

const { Text, Title } = Typography;

const DashboardAnalytics = () => {
  const { user } = useSelector((state) => state?.auth);
  const { analytics } = useSelector((state) => state?.admin);
  const { t } = useTranslation();

  // Simple loading check - show skeleton if analytics is not loaded
  const isLoading = !analytics;

  const quickActions = [
    {
      key: "tutorial",
      icon: (
        <PlayCircleOutlined style={{ fontSize: "24px", color: "#8B5CF6" }} />
      ),
      title: t("dashboard.videoTut"),
      description: t("dashboard.videoTutDesc"),
      buttonText: t("dashboard.watchNow"),
      action: () => window.open("https://youtu.be/RX5J3zBc6L4", "_blank"),
      buttonStyle: { background: "linear-gradient(135deg, #8B5CF6, #7C3AED)", border: "none" },
    },
    {
      key: "community",
      icon: <TeamOutlined style={{ fontSize: "24px", color: "#EC4899" }} />,
      title: t("dashboard.community"),
      description: t("dashboard.communityDesc"),
      buttonText: t("dashboard.joinNow"),
      action: () => window.open("https://discord.gg/USz9MNW2Ed", "_blank"),
      buttonStyle: { background: "linear-gradient(135deg, #EC4899, #DB2777)", border: "none" },
    },
    {
      key: "help",
      icon: (
        <QuestionCircleOutlined
          style={{ fontSize: "24px", color: "#A855F7" }}
        />
      ),
      title: t("dashboard.helpCenter"),
      description: t("dashboard.helpCenterDesc"),
      buttonText: t("dashboard.learnMore"),
      action: () =>
        window.open(
          "https://resources.copilotly.com/resources/installation/chrome-web-store",
          "_blank"
        ),
      buttonStyle: { background: "linear-gradient(135deg, #A855F7, #9333EA)", border: "none" },
    },
  ];

  const browsers = [
    {
      name: "Chrome",
      icon: Chrome,
      tooltip: t("dashboard.chromeTT"),
      action: () =>
        window.open(
          "https://chrome.google.com/webstore/detail/copilotly-your-personal-a/fnpfnkfggchkcaheehdgmdapnomokajo",
          "_blank"
        ),
    },
    {
      name: "Firefox",
      icon: Firefox,
      tooltip: t("dashboard.firefoxTT"),
      action: () =>
        window.open(
          "https://addons.mozilla.org/en-US/firefox/addon/copilotly-1/",
          "_blank"
        ),
    },
    {
      name: "Edge",
      icon: Edge,
      tooltip: t("dashboard.edgeTT"),
      action: () =>
        window.open(
          "https://chrome.google.com/webstore/detail/copilotly-your-personal-a/fnpfnkfggchkcaheehdgmdapnomokajo",
          "_blank"
        ),
    },
    {
      name: "Brave",
      icon: Brave,
      tooltip: t("dashboard.braveTT"),
      action: () =>
        window.open(
          "https://chrome.google.com/webstore/detail/copilotly-your-personal-a/fnpfnkfggchkcaheehdgmdapnomokajo",
          "_blank"
        ),
    },
    {
      name: "Opera",
      icon: Opera,
      tooltip: t("dashboard.operaTT"),
      action: () =>
        window.open(
          "https://chrome.google.com/webstore/detail/copilotly-your-personal-a/fnpfnkfggchkcaheehdgmdapnomokajo",
          "_blank"
        ),
    },
    {
      name: "Safari",
      icon: Safari,
      tooltip: t("dashboard.safariTT"),
      action: () => InfoNotification(t("dashboard.safariInfoNoti")),
    },
  ];

  // Skeleton Loading UI
  const SkeletonLoader = () => (
    <div>
      {/* Usage Over Time Skeleton */}
      <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0", marginBottom: "32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "linear-gradient(135deg, #8B5CF6, #EC4899)" }} />
              <Skeleton.Input active size="default" style={{ width: 160 }} />
            </div>
            <Skeleton.Input active size="small" style={{ width: 200 }} />
          </div>
          <Skeleton.Button active style={{ width: 140 }} />
        </div>
        {/* Stats Cards Skeleton */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          {[
            { bg: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)" },
            { bg: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)" },
            { bg: "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)" },
            { bg: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" },
          ].map((item, i) => (
            <Col xs={12} sm={6} key={i}>
              <div style={{ background: item.bg, borderRadius: "12px", padding: "16px", textAlign: "center" }}>
                <Skeleton.Input active size="small" style={{ width: 60, marginBottom: "8px" }} />
                <Skeleton.Input active size="large" style={{ width: 80 }} />
              </div>
            </Col>
          ))}
        </Row>
        {/* Chart Skeleton */}
        <div style={{ height: "300px", background: "linear-gradient(180deg, #f8fafc 0%, #fff 100%)", borderRadius: "12px", display: "flex", alignItems: "flex-end", padding: "20px", gap: "8px" }}>
          {[40, 65, 45, 80, 55, 70, 90, 60, 75, 50, 85, 65].map((h, i) => (
            <div key={i} style={{ flex: 1, height: `${h}%`, background: "linear-gradient(180deg, rgba(139,92,246,0.2) 0%, rgba(139,92,246,0.05) 100%)", borderRadius: "4px 4px 0 0", animation: "pulse 1.5s ease-in-out infinite", animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      </div>

      {/* Download & Plan Section Skeleton */}
      <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
        <Col xs={24} lg={17}>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ width: "26px", height: "26px", borderRadius: "6px", background: "#8B5CF6", marginRight: "12px" }} />
              <Skeleton.Input active size="default" style={{ width: 180 }} />
            </div>
            <Skeleton.Input active size="small" style={{ width: 240, marginBottom: "16px" }} />
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
              {[1, 2, 3].map((i) => (
                <Skeleton.Button key={i} active size="small" shape="round" style={{ width: 120 }} />
              ))}
            </div>
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} style={{ width: "84px", height: "84px", borderRadius: "16px", background: "#f8f9fa", border: "1px solid #e9ecef" }} />
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
                  <div>
                    <Skeleton.Input active size="default" style={{ width: 60, marginBottom: "4px" }} />
                    <Skeleton.Input active size="small" style={{ width: 80 }} />
                  </div>
                  <div>
                    <Skeleton.Input active size="default" style={{ width: 60, marginBottom: "4px" }} />
                    <Skeleton.Input active size="small" style={{ width: 80 }} />
                  </div>
                </div>
                <Skeleton.Input active size="small" style={{ width: 200 }} />
              </div>
              <Skeleton.Button active style={{ width: 120, height: 48 }} />
            </div>
          </div>
        </Col>
        <Col xs={24} lg={7}>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0", height: "100%" }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </Col>
      </Row>

      {/* Gamification Section Skeleton */}
      <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
        {[
          { bg: "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)", iconBg: "#F59E0B" },
          { bg: "linear-gradient(135deg, #f5f3ff 0%, #fdf2f8 100%)", iconBg: "#8B5CF6" },
          { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", iconBg: "#10B981" },
        ].map((item, i) => (
          <Col xs={24} sm={24} md={8} key={i}>
            <div style={{ background: item.bg, borderRadius: "16px", padding: "20px", height: "100%", minHeight: "200px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: item.iconBg }} />
                <div>
                  <Skeleton.Input active size="small" style={{ width: 100, marginBottom: "4px" }} />
                  <Skeleton.Input active size="small" style={{ width: 140 }} />
                </div>
              </div>
              <Skeleton active paragraph={{ rows: 3 }} title={false} />
            </div>
          </Col>
        ))}
      </Row>

      {/* Analytics Charts Section Skeleton */}
      <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
        {[1, 2].map((i) => (
          <Col xs={24} lg={12} key={i}>
            <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "linear-gradient(135deg, #8B5CF6, #EC4899)" }} />
                <Skeleton.Input active size="default" style={{ width: 140 }} />
              </div>
              <div style={{ height: "280px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: "200px", height: "200px", borderRadius: "50%", background: "linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(236,72,153,0.1) 100%)", border: "20px solid rgba(139,92,246,0.15)" }} />
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Quick Actions Section Skeleton */}
      <div style={{ background: "#fff", borderRadius: "16px", padding: "32px", border: "1px solid #f0f0f0", marginBottom: "32px" }}>
        <Skeleton.Input active size="default" style={{ width: 120, marginBottom: "24px" }} />
        <Row gutter={[24, 24]}>
          {[
            { color: "#8B5CF6" },
            { color: "#EC4899" },
            { color: "#A855F7" },
          ].map((item, i) => (
            <Col xs={24} md={8} key={i}>
              <div style={{ padding: "24px", border: "1px solid #f0f0f0", borderRadius: "12px", textAlign: "center" }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: `${item.color}20`, margin: "0 auto 20px" }} />
                <Skeleton.Input active size="default" style={{ width: 120, marginBottom: "12px" }} />
                <Skeleton active paragraph={{ rows: 2, width: ["100%", "80%"] }} title={false} style={{ marginBottom: "20px" }} />
                <Skeleton.Button active style={{ width: "100%", height: 40 }} />
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Community & Review Section Skeleton */}
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <div style={{ background: "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)", borderRadius: "16px", padding: "32px", textAlign: "center" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(255,255,255,0.2)", margin: "0 auto 16px" }} />
            <Skeleton.Input active style={{ width: 200, marginBottom: "12px", background: "rgba(255,255,255,0.2)" }} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ marginBottom: "24px" }} />
            <Skeleton.Button active style={{ width: 140, height: 44, background: "rgba(255,255,255,0.9)" }} />
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div style={{ background: "linear-gradient(135deg, #fdf2f8 0%, #f5f3ff 100%)", borderRadius: "16px", padding: "32px", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: "-8px", marginBottom: "16px" }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#e2e8f0", border: "2px solid #fff", marginLeft: i > 1 ? "-8px" : "0" }} />
              ))}
            </div>
            <Skeleton.Input active size="default" style={{ width: 180, marginBottom: "12px" }} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ marginBottom: "24px" }} />
            <Skeleton.Button active style={{ width: 120, height: 44 }} />
          </div>
        </Col>
      </Row>

      {/* Pulse animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );

  return (
    <div>
      {isLoading && <SkeletonLoader />}
      {!isLoading && (
        <>
          {/* Primary Analytics Section */}
          <div style={{ marginBottom: "32px" }}>
            <UsageOverTime analytics={analytics} />
          </div>

          {/* Download & Plan Section */}
          <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
            {/* Extension Download Section - Only for admins, taking 70% */}
            {user?.role === "admin" && (
              <Col xs={24} lg={17}>
                <Card
                  style={{
                    background: "white",
                    border: "1px solid #e8e8e8",
                    borderRadius: "16px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    height: "100%",
                  }}
                  bodyStyle={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {/* Header Section */}
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <DownloadOutlined
                          style={{
                            fontSize: "26px",
                            marginRight: "12px",
                            color: "#8B5CF6",
                          }}
                        />
                        <Title
                          level={4}
                          style={{
                            color: "#262626",
                            margin: 0,
                            fontSize: "22px",
                            fontWeight: "600",
                          }}
                        >
                          {t("dashboard.download")}
                        </Title>
                      </div>
                      <Text
                        style={{
                          color: "#8c8c8c",
                          fontSize: "16px",
                          fontWeight: "400",
                          display: "block",
                          marginBottom: "12px",
                        }}
                      >
                        {t("dashboard.available")}
                      </Text>

                      {/* Feature highlights */}
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                        }}
                      >
                        <div
                          style={{
                            background: "linear-gradient(135deg, #f5f3ff, #fdf2f8)",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #e9d5ff",
                          }}
                        >
                          <Text
                            style={{
                              color: "#7C3AED",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            ⚡ {t("dashboard.instantAIWriting")}
                          </Text>
                        </div>
                        <div
                          style={{
                            background: "linear-gradient(135deg, #fdf2f8, #f5f3ff)",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #fbcfe8",
                          }}
                        >
                          <Text
                            style={{
                              color: "#DB2777",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            🎯 {t("dashboard.smartSuggestions")}
                          </Text>
                        </div>
                        <div
                          style={{
                            background: "linear-gradient(135deg, #f5f3ff, #fdf2f8)",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #e9d5ff",
                          }}
                        >
                          <Text
                            style={{
                              color: "#8B5CF6",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            🔒 {t("dashboard.privacyFirst")}
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* Browser Icons Section */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          flex: 1,
                        }}
                      >
                        {browsers.map((browser, index) => (
                          <Tooltip
                            key={index}
                            title={browser.tooltip}
                            placement="top"
                          >
                            <div
                              style={{
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                padding: "16px",
                                borderRadius: "16px",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e9ecef",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "84px",
                                height: "84px",
                              }}
                              onClick={browser.action}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(-4px)";
                                e.currentTarget.style.boxShadow =
                                  "0 8px 24px rgba(139, 92, 246, 0.25)";
                                e.currentTarget.style.backgroundColor =
                                  "#f5f3ff";
                                e.currentTarget.style.borderColor = "#8B5CF6";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.backgroundColor =
                                  "#f8f9fa";
                                e.currentTarget.style.borderColor = "#e9ecef";
                              }}
                            >
                              <img
                                src={browser.icon}
                                alt={browser.name}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          </Tooltip>
                        ))}
                      </div>
                    </div>

                    {/* Stats and CTA Section */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        marginTop: "auto",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "24px",
                            marginBottom: "12px",
                          }}
                        >
                          <div>
                            <Text
                              style={{
                                color: "#8B5CF6",
                                fontSize: "24px",
                                fontWeight: "600",
                                display: "block",
                              }}
                            >
                              50K+
                            </Text>
                            <Text
                              style={{
                                color: "#8c8c8c",
                                fontSize: "13px",
                                fontWeight: "500",
                              }}
                            >
                              {t("dashboard.activeUsers")}
                            </Text>
                          </div>
                          <div>
                            <Text
                              style={{
                                color: "#EC4899",
                                fontSize: "24px",
                                fontWeight: "600",
                                display: "block",
                              }}
                            >
                              4.9★
                            </Text>
                            <Text
                              style={{
                                color: "#8c8c8c",
                                fontSize: "13px",
                                fontWeight: "500",
                              }}
                            >
                              {t("dashboard.chromeRating")}
                            </Text>
                          </div>
                        </div>
                        <Text
                          style={{
                            color: "#595959",
                            fontSize: "14px",
                            fontWeight: "400",
                            fontStyle: "italic",
                          }}
                        >
                          {t("dashboard.chromeReviewQuote")}
                        </Text>
                      </div>

                      <div style={{ marginLeft: "24px" }}>
                        <Button
                          type="primary"
                          size="large"
                          style={{
                            background: "linear-gradient(135deg, #8B5CF6, #EC4899)",
                            border: "none",
                            borderRadius: "8px",
                            fontWeight: "600",
                            height: "48px",
                            paddingLeft: "32px",
                            paddingRight: "32px",
                            fontSize: "15px",
                            boxShadow: "0 4px 14px rgba(139, 92, 246, 0.4)",
                          }}
                          onClick={() =>
                            window.open(
                              "https://chrome.google.com/webstore/detail/copilotly-your-personal-a/fnpfnkfggchkcaheehdgmdapnomokajo",
                              "_blank"
                            )
                          }
                        >
                          {t("dashboard.installNow")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}

            {/* Current Plan Section - Taking 30% */}
            <Col xs={24} lg={user?.role === "admin" ? 7 : 24}>
              <UpgradePlanCard user={user} analytics={analytics} />
            </Col>
          </Row>

          {/* Gamification Section */}
          <Row
            gutter={[24, 24]}
            style={{
              marginBottom: "32px",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Col xs={24} sm={24} md={8} style={{ display: "flex" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <DailyRewardsCard />
              </div>
            </Col>
            <Col xs={24} sm={24} md={8} style={{ display: "flex" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <AchievementsCard />
              </div>
            </Col>
            <Col xs={24} sm={24} md={8} style={{ display: "flex" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ReferralCard />
              </div>
            </Col>
          </Row>

          {/* Analytics Charts Section */}
          <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
            <Col xs={24} lg={12}>
              <CategoriesUsageCard analytics={analytics} />
            </Col>
            <Col xs={24} lg={12}>
              <CopilotsUsageCard analytics={analytics} />
            </Col>
          </Row>

          {/* Quick Actions Section */}
          <Row gutter={[24, 24]} style={{ marginBottom: "32px" }}>
            <Col span={24}>
              <Card
                style={{
                  borderRadius: "16px",
                  border: "1px solid #f0f0f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  background: "white",
                }}
                bodyStyle={{ padding: "32px" }}
              >
                <Title
                  level={4}
                  style={{
                    marginBottom: "24px",
                    color: "#262626",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  {t("dashboard.quickActions")}
                </Title>
                <Row
                  gutter={[24, 24]}
                  style={{ display: "flex", alignItems: "stretch" }}
                >
                  {quickActions.map((action) => (
                    <Col
                      xs={24}
                      md={8}
                      key={action.key}
                      style={{ display: "flex" }}
                    >
                      <div
                        style={{
                          padding: "24px",
                          border: "1px solid #f0f0f0",
                          borderRadius: "12px",
                          textAlign: "center",
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                          background: "white",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#8B5CF6";
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(139, 92, 246, 0.2)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#f0f0f0";
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              marginBottom: "20px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "48px",
                            }}
                          >
                            <div style={{ fontSize: "24px", color: "#8B5CF6" }}>
                              {action.icon}
                            </div>
                          </div>
                          <Title
                            level={5}
                            style={{
                              marginBottom: "12px",
                              color: "#262626",
                              fontSize: "16px",
                              fontWeight: "600",
                            }}
                          >
                            {action.title}
                          </Title>
                          <Text
                            style={{
                              color: "#8c8c8c",
                              fontSize: "14px",
                              display: "block",
                              marginBottom: "20px",
                              lineHeight: "1.5",
                              fontWeight: "400",
                            }}
                          >
                            {action.description}
                          </Text>
                        </div>
                        <Button
                          type="primary"
                          style={{
                            ...action.buttonStyle,
                            borderRadius: "8px",
                            fontWeight: "500",
                            height: "40px",
                            fontSize: "14px",
                            width: "100%",
                          }}
                          onClick={action.action}
                        >
                          {action.buttonText}
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Community & Review Section */}
          <Row gutter={[24, 24]} style={{ alignItems: "stretch" }}>
            <Col xs={24} md={12} style={{ display: "flex" }}>
              <Card
                style={{
                  borderRadius: "16px",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)",
                  color: "white",
                  boxShadow: "0 4px 20px rgba(124, 58, 237, 0.35)",
                  width: "100%",
                }}
                bodyStyle={{ padding: "32px" }}
              >
                <div style={{ textAlign: "center" }}>
                  <GiftOutlined
                    style={{ fontSize: "32px", marginBottom: "16px" }}
                  />
                  <Title
                    level={4}
                    style={{
                      color: "white",
                      marginBottom: "12px",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    {t("dashboard.getFreeWords")}
                  </Title>
                  <Text
                    style={{
                      color: "rgba(255,255,255,0.9)",
                      fontSize: "15px",
                      display: "block",
                      marginBottom: "24px",
                      lineHeight: "1.5",
                      fontWeight: "400",
                    }}
                  >
                    {t("dashboard.postReview")} <br />
                    <strong>{t("dashboard.shareScreenShot")}</strong>
                  </Text>
                  <Button
                    type="primary"
                    style={{
                      background: "white",
                      color: "#7C3AED",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                      height: "44px",
                      fontSize: "15px",
                      padding: "0 24px",
                    }}
                    onClick={() =>
                      window.open(
                        "https://chrome.google.com/webstore/detail/copilotly-your-personal-a/fnpfnkfggchkcaheehdgmdapnomokajo",
                        "_blank"
                      )
                    }
                  >
                    {t("dashboard.getFreeCredits")}
                  </Button>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12} style={{ display: "flex" }}>
              <Card
                style={{
                  borderRadius: "16px",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #fdf2f8 0%, #f5f3ff 100%)",
                  boxShadow: "0 4px 12px rgba(236, 72, 153, 0.15)",
                  width: "100%",
                }}
                bodyStyle={{ padding: "32px" }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <Avatar.Group>
                      <Avatar src={slideAvatar1} size={40} />
                      <Avatar src={slideAvatar2} size={40} />
                      <Avatar src={slideAvatar3} size={40} />
                    </Avatar.Group>
                  </div>
                  <Title
                    level={4}
                    style={{
                      color: "#262626",
                      marginBottom: "12px",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    {t("dashboard.joinCommunity")}
                  </Title>
                  <Text
                    style={{
                      color: "#8c8c8c",
                      fontSize: "15px",
                      display: "block",
                      marginBottom: "24px",
                      lineHeight: "1.5",
                      fontWeight: "400",
                    }}
                  >
                    {t("dashboard.joinCommunityDesc")}
                  </Text>
                  <Button
                    type="primary"
                    icon={<FacebookOutlined />}
                    style={{
                      background: "linear-gradient(135deg, #8B5CF6, #EC4899)",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                      height: "44px",
                      fontSize: "15px",
                      padding: "0 24px",
                      boxShadow: "0 4px 14px rgba(139, 92, 246, 0.3)",
                    }}
                    onClick={() =>
                      window.open(
                        "https://www.facebook.com/groups/684411813218650/",
                        "_blank"
                      )
                    }
                  >
                    {t("dashboard.joinNow")}
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DashboardAnalytics;
